import { useState, useEffect, FormEvent } from 'react'
import type { Idea } from '../services/types'

interface IdeaFormProps {
  mode: 'create' | 'edit'
  initialData?: Idea
  onSubmit: (data: FormData) => Promise<void>
  onCancel: () => void
}

export interface FormData {
  name: string
  description: string
  category: string
  rank: number
  status: 'open' | 'partial' | 'done' | 'abandoned'
}

const statusOptions = [
  { value: 'open', label: 'Open', color: 'var(--energy-cyan)' },
  { value: 'partial', label: 'Partial', color: 'var(--alert-amber)' },
  { value: 'done', label: 'Done', color: 'var(--alert-success)' },
  { value: 'abandoned', label: 'Abandoned', color: 'var(--chrome-400)' },
]

function IdeaForm({ mode, initialData, onSubmit, onCancel }: IdeaFormProps) {
  const [formData, setFormData] = useState<FormData>({
    name: initialData?.name || '',
    description: initialData?.description || '',
    category: initialData?.category || '',
    rank: initialData?.rank || 50,
    status: initialData?.status || 'open',
  })

  const [errors, setErrors] = useState<{ name?: string }>({})
  const [isSubmitting, setIsSubmitting] = useState(false)

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onCancel()
      }
    }

    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [onCancel])

  const validate = (): boolean => {
    const newErrors: { name?: string } = {}

    if (!formData.name.trim()) {
      newErrors.name = 'Name required'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()

    if (!validate()) {
      return
    }

    setIsSubmitting(true)
    try {
      await onSubmit(formData)
    } catch (error) {
      console.error('Form submission error:', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  const currentStatus = statusOptions.find(s => s.value === formData.status)

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Name Field */}
      <div className="space-y-2">
        <label htmlFor="name" className="flex items-center gap-2">
          <span className="w-1.5 h-1.5 bg-[var(--energy-cyan)] rotate-45" />
          <span>Name</span>
          <span className="text-[var(--alert-coral)] text-xs">*</span>
        </label>
        <input
          type="text"
          id="name"
          value={formData.name}
          onChange={(e) => {
            setFormData({ ...formData, name: e.target.value })
            if (errors.name) {
              setErrors({})
            }
          }}
          disabled={isSubmitting}
          placeholder="Enter idea name"
          className={errors.name ? 'border-[var(--alert-coral)] focus:border-[var(--alert-coral)]' : ''}
        />
        {errors.name && (
          <div className="flex items-center gap-2 text-[var(--alert-coral)] text-sm">
            <span className="w-1 h-1 bg-[var(--alert-coral)] rounded-full animate-pulse" />
            <span>{errors.name}</span>
          </div>
        )}
      </div>

      {/* Description Field */}
      <div className="space-y-2">
        <label htmlFor="description" className="flex items-center gap-2">
          <span className="w-1.5 h-1.5 bg-[var(--weave-400)] rotate-45" />
          <span>Description</span>
        </label>
        <textarea
          id="description"
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          rows={4}
          disabled={isSubmitting}
          placeholder="Describe your idea..."
          className="resize-none"
        />
      </div>

      {/* Category Field */}
      <div className="space-y-2">
        <label htmlFor="category" className="flex items-center gap-2">
          <span className="w-1.5 h-1.5 bg-[var(--weave-400)] rotate-45" />
          <span>Category</span>
        </label>
        <input
          type="text"
          id="category"
          value={formData.category}
          onChange={(e) => setFormData({ ...formData, category: e.target.value })}
          disabled={isSubmitting}
          placeholder="e.g., Feature, Bug, Research"
        />
      </div>

      {/* Rank Slider */}
      <div className="space-y-3">
        <label htmlFor="rank" className="flex items-center justify-between">
          <span className="flex items-center gap-2">
            <span className="w-1.5 h-1.5 bg-[var(--weave-400)] rotate-45" />
            <span>Priority Rank</span>
          </span>
          <span className="rank-indicator">{formData.rank}</span>
        </label>
        <div className="relative">
          <input
            type="range"
            id="rank"
            min="1"
            max="100"
            value={formData.rank}
            onChange={(e) => setFormData({ ...formData, rank: parseInt(e.target.value) })}
            disabled={isSubmitting}
            className="w-full"
          />
          {/* Range labels */}
          <div className="flex justify-between text-xs text-[var(--chrome-400)] mt-1">
            <span>Low</span>
            <span>Medium</span>
            <span>High</span>
          </div>
        </div>
      </div>

      {/* Status Field */}
      <div className="space-y-2">
        <label htmlFor="status" className="flex items-center gap-2">
          <span 
            className="w-1.5 h-1.5 rotate-45 transition-colors"
            style={{ backgroundColor: currentStatus?.color }}
          />
          <span>Status</span>
        </label>
        <select
          id="status"
          value={formData.status}
          onChange={(e) => setFormData({ ...formData, status: e.target.value as FormData['status'] })}
          disabled={isSubmitting}
        >
          {statusOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      {/* Divider */}
      <div className="flex items-center gap-4 py-2">
        <div className="flex-1 h-[1px] bg-gradient-to-r from-transparent via-[var(--glass-border)] to-transparent" />
        <div className="w-1 h-1 bg-[var(--weave-400)] rotate-45" />
        <div className="flex-1 h-[1px] bg-gradient-to-r from-transparent via-[var(--glass-border)] to-transparent" />
      </div>

      {/* Action Buttons */}
      <div className="flex justify-end gap-3">
        <button
          type="button"
          onClick={onCancel}
          disabled={isSubmitting}
          className="btn-ghost"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={isSubmitting}
          className="btn-primary relative overflow-hidden group"
        >
          {/* Shimmer effect */}
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
          
          {isSubmitting ? (
            <span className="flex items-center gap-2">
              <span className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              <span>Processing...</span>
            </span>
          ) : (
            <span className="flex items-center gap-2">
              <span>{mode === 'create' ? 'Initialize' : 'Update'}</span>
              <span className="text-xs opacity-70">→</span>
            </span>
          )}
        </button>
      </div>
    </form>
  )
}

export default IdeaForm
